import axios from "axios";
import jwtDecode from "jwt-decode";
import config, { getServiceBaseUrl } from "./config";
import ApiError from "./ApiError";

/**
 * Shared refresh token logic to avoid duplication
 */
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token) => {
  refreshSubscribers.map((cb) => cb(token));
  refreshSubscribers = [];
};

const handleLogout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  localStorage.setItem("session_expire", "Y");
  window.location.reload();
};

const refreshToken = async () => {
  const refresh_token = localStorage.getItem("refresh_token");
  if (!refresh_token) {
    handleLogout();
    return null;
  }

  try {
    // Use a fresh axios instance for refresh to avoid interceptor loops
    const adminBaseUrl = getServiceBaseUrl("ADMIN_V1");
    const response = await axios.post(`${adminBaseUrl}/refresh-token`, {
      refresh_token,
    });

    if (response?.data?.success) {
      const newToken = response.data.data.token;
      localStorage.setItem("token", newToken);
      return newToken;
    } else {
      handleLogout();
      return null;
    }
  } catch (error) {
    handleLogout();
    return null;
  }
};

/**
 * Creates a specialized axios client for a specific service
 */
const createClient = (serviceName) => {
  const baseURL = getServiceBaseUrl(serviceName);

  const client = axios.create({
    baseURL,
    timeout: 30000, // 30 seconds default timeout
    headers: {
      "Content-Type": "application/json",
      isAdmin: "true",
    },
  });

  // Request Interceptor: Auth & Token Validation
  client.interceptors.request.use(
    async (axiosConfig) => {
      const token = localStorage.getItem("token");

      if (token) {
        try {
          const payload = jwtDecode(token);
          const isExpired = payload.exp * 1000 < Date.now();

          if (isExpired && !axiosConfig._retry) {
            if (!isRefreshing) {
              isRefreshing = true;
              const newToken = await refreshToken();
              isRefreshing = false;
              if (newToken) {
                onRefreshed(newToken);
              }
            }

            const retryOrigRequest = new Promise((resolve) => {
              subscribeTokenRefresh((token) => {
                axiosConfig.headers.Authorization = `Bearer ${token}`;
                resolve(axiosConfig);
              });
            });
            return retryOrigRequest;
          }

          axiosConfig.headers.Authorization = `Bearer ${token}`;
        } catch (e) {
          console.error(`[${serviceName}] Token processing error:`, e);
        }
      }

      return axiosConfig;
    },
    (error) => Promise.reject(error)
  );

  // Response Interceptor: Data Normalization, Error Handling & Retry
  client.interceptors.response.use(
    (response) => {
      if (response.data && response.data.success === false) {
        throw new ApiError(response.data.message || "Operation failed", {
          service: serviceName,
          endpoint: response.config.url,
          status: response.status,
          data: response.data,
        });
      }
      return response.data;
    },
    async (error) => {
      const { config: axiosConfig } = error;

      // Retry Logic: Only for GET requests or specific error codes if configured
      if (axiosConfig && axiosConfig.retry && axiosConfig.retryCount < axiosConfig.retry) {
        axiosConfig.retryCount = axiosConfig.retryCount || 0;
        axiosConfig.retryCount += 1;

        console.warn(`[${serviceName}] Retrying request (${axiosConfig.retryCount}/${axiosConfig.retry}): ${axiosConfig.url}`);

        // Exponential backoff
        const delay = Math.pow(2, axiosConfig.retryCount) * 500;
        await new Promise(resolve => setTimeout(resolve, delay));

        return client(axiosConfig);
      }

      const status = error.response ? error.response.status : null;
      const data = error.response ? error.response.data : null;
      const message = data?.message || error.message || "An unexpected error occurred";

      const apiError = new ApiError(message, {
        service: serviceName,
        endpoint: error.config?.url,
        status,
        data
      });

      console.error(`[${serviceName} Client Error]:`, apiError.toJSON());
      return Promise.reject(apiError);
    }
  );

  return client;
};

// Singleton instances for each service
export const userClient = createClient("USER");
export const paymentClient = createClient("PAYMENT");
export const adminClient = createClient("ADMIN");
export const adminV1Client = createClient("ADMIN_V1");
export const feedClient = createClient("FEED");
export const followClient = createClient("FOLLOW");
export const baseClient = createClient("BASE");
export const rootClient = createClient("ROOT");
export const authClient = createClient("AUTH");

export default {
  user: userClient,
  payment: paymentClient,
  admin: adminClient,
  adminV1: adminV1Client,
  feed: feedClient,
  follow: followClient,
  base: baseClient,
  root: rootClient,
  auth: authClient,
};
